import { PAGE_TITLES } from '../constants/pageTracking';
import { facebookPixelEvents } from './facebookPixel';
import { trackGAPageView, trackGAEvent } from './googleAnalytics';
import { hasConsent } from './consentManager';

/**
 * Handle Facebook Click ID from URL parameters
 * Only stores cookie if marketing consent is given
 */
const handleFacebookClickId = (): void => {
    // Only process Facebook Click ID if user has given marketing consent
    if (!hasConsent('marketing')) {
        return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const fbclid = urlParams.get('fbclid');

    if (fbclid) {
        // Store fbclid as fbc cookie (90 days expiry per Facebook standard)
        const now = new Date();
        const expires = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000);
        document.cookie = `_fbc=fb.1.${Date.now()}.${fbclid}; expires=${expires.toUTCString()}; path=/; SameSite=Lax; Secure`;
    }
};

/**
 * Track page view across all enabled tracking platforms
 * Respects user consent - only tracks on platforms where consent is given
 *
 * GDPR/CCPA Compliant - No tracking without consent
 * IP addresses are anonymized in Google Analytics
 */
export const trackPageView = (pageKey: keyof typeof PAGE_TITLES, pageSection: string): void => {
    try {
        // Handle Facebook click ID (only if marketing consent given)
        handleFacebookClickId();

        const pageTitle = PAGE_TITLES[pageKey]?.tracking || pageKey;

        // Facebook Pixel tracking (requires marketing consent)
        if (hasConsent('marketing')) {
            facebookPixelEvents.viewContent({
                content_name: pageTitle,
                content_category: pageSection,
                page_title: pageTitle
            });
        }

        // Google Analytics tracking (requires analytics consent)
        // IP is automatically anonymized in googleAnalytics.ts
        if (hasConsent('analytics')) {
            trackGAPageView(pageTitle, window.location.pathname, {
                page_type: pageSection,
                // Only include fbclid if marketing consent given
                ...(hasConsent('marketing') && {
                    fbclid: new URLSearchParams(window.location.search).get('fbclid') || undefined
                })
            });
        }
    } catch (error) {
        console.error('Error tracking page view:', error);
    }
};

/**
 * Track user interactions (button clicks, form submissions, etc.)
 * Respects user consent - only tracks on platforms where consent is given
 *
 * GDPR/CCPA Compliant - No tracking without consent
 * IP addresses are NOT collected (privacy by design)
 */
export const trackInteraction = (
    action: string,
    pageKey: keyof typeof PAGE_TITLES,
    additionalData?: Record<string, any>
): void => {
    try {
        const pageTitle = PAGE_TITLES[pageKey]?.tracking || pageKey;

        // Facebook Pixel tracking (requires marketing consent)
        if (hasConsent('marketing')) {
            facebookPixelEvents.lead({
                content_name: pageTitle,
                ...additionalData
            });
        }

        // Google Analytics tracking (requires analytics consent)
        if (hasConsent('analytics')) {
            trackGAEvent(action, {
                page_title: pageTitle,
                ...additionalData
            });
        }
    } catch (error) {
        console.error('Error tracking interaction:', error);
    }
}; 